import type { DownloadInformationData } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { Box, type SxProps, type Theme } from '@mui/material';
import type { TFunction } from 'i18next';
import _, { noop } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { storeDownloadedItem as storeDownloadedItemAction } from '../../actions/download.actions';
import { incrementDownloadsCounter as incrementDownloadsCounterAction } from '../../actions/user.actions';
import { customSizes, palette, spaces } from '../../assets/styles/themes/tokens';
import {
	selectIsItemInWhichOwnCollections as selectIsItemInWhichOwnCollectionsAction,
	selectOwnCollectionStatus as selectOwnCollectionStatusAction,
} from '../../reducers/collections.selectors';
import { selectMindItem } from '../../reducers/mindItems.selectors';
import { selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { getAmpliDeviceId, getAmpliSessionId, getCurrentSearchId } from '../../utils/ampli';
import { getPositionFromQuery, getPositionFromState } from '../../utils/searchUtils';
import useAction from '../../utils/useAction';
import { useNavigationLayout } from '../../utils/useNavigationLayout';
import useRouter from '../../utils/useRouter';
import useScreenSize from '../../utils/useScreenSize';
import type { PinAction } from '../cards/MuiCard/MuiCardBase/MuiCardBase';
import NotificationAlert from '../NotificationAlert';
import type { Alert } from '../NotificationAlert/NotificationAlert';
import { ReaderContext } from '../Reader/ReaderContext';
import UpgradeOfferAlert from '../UpgradeOfferAlert';
import initFileDownload from '../utils/downloadIFrame';
import BasisReaderContent from './BasisReaderContent';
import FloatingToolbar from './FloatingToolbar/FloatingToolbarBase';
import PagePreviewList from './PagePreviewList';
import ReaderContent from './ReaderContent/ReaderContent';
import ReaderDetailsWrapper from './ReaderDetails/ReaderDetailsWrapper';
import ReaderVideoPlayer from './ReaderVideoPlayer';
import ReturnTopButton from './ReturnTopButton/ReturnTopButton';
import Toolbar from './Toolbar/Toolbar';
import VideoReaderButtons from './VideoReaderButtons/VideoReaderButtons';

interface TrackingInfo {
	sessionId?: number;
	deviceId?: string;
	searchId?: string;
	position?: number;
}

const getDownloadAlerts = (
	t: TFunction,
	leftDownloads: number,
	downloadLimit: number
): { [key: string]: Alert } => ({
	downloadSuccessAlert: {
		message: t('You still have <number> of <limit> downloads left this month', {
			number: leftDownloads,
			limit: downloadLimit,
		}),
		severity: 'success',
	},
	downloadLimitReached: {
		message: t(
			'Your download limit for this month has been reached. ' +
				'Starting next month you will be able to download new materials again.'
		),
		severity: 'warning',
	},
});

export const getPinnedAlerts = (t: TFunction): { [key: string]: Alert } => ({
	addedToFavAlert: {
		message: t('The material has been successfully added to the favorites.'),
		severity: 'success',
	},
	removedFromFavAlert: {
		message: t('The material has been successfully removed from the favorites.'),
		severity: 'success',
	},
});

const getCollectionAlerts = (t: TFunction): { [key: string]: Alert } => ({
	updatedCollections: {
		message: t('Your changes have been successfully saved.'),
		severity: 'success',
	},
});

const NewReader = ({ isPublicVideoPlayer = false }: { isPublicVideoPlayer?: boolean }) => {
	const { match } = useRouter();
	const {
		location: { query, state },
	} = match;
	const [downloadInfo, setDownloadInfo] = useState({} as DownloadInformationData);
	const [downloadTriggered, setDownloadTriggered] = useState(false); // prevent multiple downloads from being counted
	const { renderNavigationMobileLayout } = useNavigationLayout();
	const { t } = useTranslation();
	const { isLgScreen, isMdScreen } = useScreenSize();
	const isDesktop = isLgScreen || isMdScreen;
	const {
		currentPage,
		mindItemId,
		pspdfkitInstance,
		fullScreenMode,
		alert,
		setAlert,
		zoomProperties,
		setCurrentPage,
	} = useContext(ReaderContext);
	const mindItem = useSelector((state) => selectMindItem(state, mindItemId));
	const incrementDownloadsCounter = useAction(incrementDownloadsCounterAction);
	const storeDownloadedItem = useAction(storeDownloadedItemAction);
	const selectOwnCollectionStatus = useSelector((state) => selectOwnCollectionStatusAction(state));
	const selectIsItemInWhichOwnCollections: string[] = useSelector((state) =>
		selectIsItemInWhichOwnCollectionsAction(state, mindItemId)
	);
	const [prevCollections, setPrevCollections] = useState<{
		mindItemId?: string;
		collections?: string[];
	}>({});
	const user = useSelector(selectUser);
	const basisReaderContainerRef = useRef<HTMLDivElement>(null);

	const onPinActionCallback = (action: PinAction) => {
		setAlert?.(action === 'pin' ? alerts.addedToFavAlert : alerts.removedFromFavAlert);
	};

	const searchId = getCurrentSearchId() || query?.search_id;
	const position = getPositionFromState(state) ?? getPositionFromQuery(query);
	const trackingInfo: TrackingInfo = {
		sessionId: getAmpliSessionId(),
		deviceId: getAmpliDeviceId(),
		...(searchId && { searchId }),
		...(position !== undefined && { position }),
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (selectOwnCollectionStatus === AsyncActionStatus.READY) {
			const isFirstRender = prevCollections.collections === undefined;
			const hasMindItemChanged = prevCollections.mindItemId !== mindItemId;
			const hasCollectionsChanged = !_.isEqual(
				prevCollections.collections,
				selectIsItemInWhichOwnCollections
			);

			if (hasCollectionsChanged) {
				setPrevCollections({
					mindItemId,
					collections: selectIsItemInWhichOwnCollections,
				});
				if (!isFirstRender && !hasMindItemChanged) {
					setAlert?.(alerts.updatedCollections);
				}
			}
		}
	}, [selectIsItemInWhichOwnCollections]);

	const leftDownloads =
		downloadInfo.downloadLimit -
		downloadInfo.counter -
		(downloadInfo?.mainDocument?.countsTowardsLimit ? 1 : 0); // it does not count if doc is free or already downloaded

	const alerts: { [key: string]: Alert } = {
		...getDownloadAlerts(t, leftDownloads, downloadInfo.downloadLimit),
		...getPinnedAlerts(t),
		...getCollectionAlerts(t),
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		services.mindItem
			.getDownloadInformation(mindItemId, trackingInfo)
			.then((data: DownloadInformationData) => {
				setDownloadInfo(data);
			})
			.catch(noop);

		setDownloadTriggered(false);
	}, [mindItemId]);

	const downloadMindItem = () => {
		if (leftDownloads < 0) {
			setAlert?.(alerts.downloadLimitReached);
		} else if (
			downloadInfo.canDownload &&
			downloadInfo.mainDocument?.canDownload &&
			downloadInfo.mainDocument?.link
		) {
			initFileDownload(downloadInfo.mainDocument.link);
			storeDownloadedItem(downloadInfo.mainDocument.id);

			if (!downloadTriggered && downloadInfo.mainDocument.countsTowardsLimit) {
				incrementDownloadsCounter();
			}

			setDownloadTriggered(true);
			setAlert?.(alerts.downloadSuccessAlert);
		}
	};

	const renderAlertComponent = (alert: Alert, alertSx?: SxProps<Theme>) => {
		return (
			<NotificationAlert
				message={alert.message}
				severity={alert.severity}
				onClose={() => setAlert?.(undefined)}
				alertSx={{ width: '100%', ...alertSx }}
			/>
		);
	};

	const returnTop = () => {
		if (pspdfkitInstance && user?.hasDocumentLevelAccess) {
			pspdfkitInstance?.setViewState((viewState) => viewState.set('currentPageIndex', 0));
		} else {
			setCurrentPage(1);
			basisReaderContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	if (!mindItem) return null;

	const isVideoReader = mindItem?.fileType === 'video';
	const showUpgradeAlert = isVideoReader && !user?.hasDocumentLevelAccess;

	return (
		<Box
			height="100%"
			width="100%"
			sx={{
				backgroundColor: palette.background.default,
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			{!isPublicVideoPlayer && (
				<Toolbar
					mindItem={mindItem}
					downloadMindItem={downloadMindItem}
					pinActionCallback={onPinActionCallback}
				/>
			)}
			<Box
				sx={{
					position: 'relative',
					paddingX: customSizes.readerPage.paddingX,
					display: 'flex',
					flexGrow: 1,
					overflow: 'hidden',
				}}
			>
				{
					<>
						{isDesktop && <ReaderDetailsWrapper isPublicVideoPlayer={isPublicVideoPlayer} />}
						{isVideoReader ? (
							<Box
								sx={{
									width: '100%',
									display: 'flex',
									alignItems: 'flex-start',
									justifyContent: 'center',
								}}
							>
								<Box
									sx={{
										width: '100%',
										height: '100%',
										display: 'flex',
										gap: spaces.md,
										flexDirection: 'column',
										alignItems: 'flex-start',
										paddingY: customSizes.readerPage.paddingY,
										paddingLeft: isDesktop ? customSizes.readerPage.paddingX : 0,
										maxWidth: customSizes.readerPage.video.maxWidth,
										position: 'relative',
									}}
								>
									{alert && renderAlertComponent(alert, { marginLeft: spaces.md })}
									{showUpgradeAlert && user && (
										<Box sx={{ width: '100%' }}>
											<UpgradeOfferAlert user={user} />
										</Box>
									)}
									<Box
										sx={{
											maxHeight: customSizes.readerPage.video.maxHeight,
											width: '100%',
										}}
									>
										<ReaderVideoPlayer
											mindItem={mindItem}
											isPublicVideoPlayer={isPublicVideoPlayer}
											variant={renderNavigationMobileLayout ? 'mobile' : 'desktop'}
										/>
									</Box>
									<Box
										sx={{
											display: 'flex',
											width: '100%',
											justifyContent: 'center',
										}}
									>
										<VideoReaderButtons />
									</Box>
								</Box>
							</Box>
						) : (
							<Box
								sx={{
									display: 'flex',
									flex: 1,
									height: '100%',
									position: 'relative',
									marginX: isDesktop && !fullScreenMode ? customSizes.pdfArea.paddingX : 0,
									justifyContent: 'center',
									alignItems: 'center',
									overflow: 'hidden',
								}}
							>
								{alert && renderAlertComponent(alert)}
								{user?.hasDocumentLevelAccess ? (
									<ReaderContent />
								) : (
									<BasisReaderContent
										containerRef={basisReaderContainerRef}
										pages={'pages' in mindItem ? mindItem.pages : []}
										zoom={zoomProperties.zoomFactor}
										isReadOnly={mindItem.isReadOnly}
										downloadMindItem={downloadMindItem}
									/>
								)}
								<FloatingToolbar mindItem={mindItem} />
								{currentPage > 1 && (
									<ReturnTopButton onClick={returnTop} isFullScreenMode={!!fullScreenMode} />
								)}
							</Box>
						)}
						{isDesktop && !isVideoReader && <PagePreviewList />}
					</>
				}
			</Box>
		</Box>
	);
};

export default NewReader;
